import type {
  AdminUserCreate,
  AdminUserDetail,
  AdminUserListItem,
  AdminUserPatch,
  AlertGenSummary,
  AlertRow,
  BuildingRef,
  DashboardDueSoonItem,
  DashboardWorkOrderWeekItem,
  DashboardLeadFollowUpItem,
  DashboardLowInventoryItem,
  DashboardRecentIncidentItem,
  DashboardSummary,
  InventoryCreate,
  InventoryItemDetail,
  InventoryListItem,
  InventoryPatch,
  EvictionEntry,
  IncidentCreate,
  IncidentDetail,
  IncidentListItem,
  IncidentPatch,
  Lead,
  LeadCreate,
  LeadPatch,
  LeadWithNotes,
  MaintenanceRef,
  TenantRef,
  TenantCreate,
  TenantDetail,
  TenantListItem,
  TenantPatch,
  UnitDetail,
  UnitListItem,
  UnitPatch,
  MyWorkOrderDetail,
  MyWorkOrderListItem,
  MyWorkOrderUpdate,
  SessionUser,
  UnitRef,
  WorkOrderCreate,
  WorkOrderDetail,
  WorkOrderListItem,
  WorkOrderPatch,
  WorkOrderTimesheetRow,
} from "./types";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? safeJsonParse(text) : null;
  if (!res.ok) {
    const message =
      body && typeof body === "object" && "error" in body && typeof body.error === "string"
        ? body.error
        : `request_failed_${res.status}`;
    throw new ApiError(res.status, body, message);
  }
  return body as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  health: () => request<{ ok: boolean; name: string; version: string }>("/health"),

  login: (email: string, password: string) =>
    request<{ user: SessionUser }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),

  me: () => request<{ user: SessionUser }>("/auth/me"),

  dashboardSummary: () => request<DashboardSummary>("/dashboard/summary"),

  dashboard: {
    dueSoon: () => request<{ items: DashboardDueSoonItem[] }>("/dashboard/due-soon"),
    workOrderWeek: () =>
      request<{
        startDate: string;
        endDate: string;
        items: DashboardWorkOrderWeekItem[];
      }>("/dashboard/work-order-week"),
    lowInventory: () =>
      request<{ items: DashboardLowInventoryItem[] }>("/dashboard/low-inventory"),
    leadFollowUps: () =>
      request<{ items: DashboardLeadFollowUpItem[] }>("/dashboard/lead-follow-ups"),
    recentIncidents: () =>
      request<{ items: DashboardRecentIncidentItem[] }>("/dashboard/recent-incidents"),
  },

  workOrders: {
    list: (params: { building?: string; status?: string; due?: string }) => {
      const qs = new URLSearchParams();
      if (params.building) qs.set("building", params.building);
      if (params.status) qs.set("status", params.status);
      if (params.due) qs.set("due", params.due);
      return request<{ workOrders: WorkOrderListItem[] }>(`/work-orders?${qs.toString()}`);
    },
    get: (id: number) => request<{ workOrder: WorkOrderDetail }>(`/work-orders/${id}`),
    create: (body: WorkOrderCreate) =>
      request<{ id: number }>("/work-orders", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (id: number, patch: WorkOrderPatch) =>
      request<{ ok: true; statusChanged: boolean }>(`/work-orders/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },

  incidents: {
    list: (params: { building?: string; status?: string; severity?: string }) => {
      const qs = new URLSearchParams();
      if (params.building) qs.set("building", params.building);
      if (params.status) qs.set("status", params.status);
      if (params.severity) qs.set("severity", params.severity);
      return request<{ incidents: IncidentListItem[] }>(`/incidents?${qs.toString()}`);
    },
    get: (id: number) => request<{ incident: IncidentDetail }>(`/incidents/${id}`),
    create: (body: IncidentCreate) =>
      request<{ id: number }>("/incidents", { method: "POST", body: JSON.stringify(body) }),
    update: (id: number, patch: IncidentPatch) =>
      request<{ ok: true }>(`/incidents/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    addNote: (id: number, note: string) =>
      request<{ ok: true }>(`/incidents/${id}/notes`, {
        method: "POST",
        body: JSON.stringify({ note }),
      }),
  },

  admin: {
    list: () => request<{ users: AdminUserListItem[] }>("/admin/users"),
    get: (id: number) => request<{ user: AdminUserDetail }>(`/admin/users/${id}`),
    create: (body: AdminUserCreate) =>
      request<{ id: number }>("/admin/users", { method: "POST", body: JSON.stringify(body) }),
    update: (id: number, patch: AdminUserPatch) =>
      request<{ ok: true }>(`/admin/users/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    resetPassword: (id: number, password: string) =>
      request<{ ok: true }>(`/admin/users/${id}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ password }),
      }),
    timesheets: () =>
      request<{ entries: WorkOrderTimesheetRow[] }>("/admin/users/work-order-timesheets"),
  },

  alerts: {
    list: (includeAll = false) => {
      const qs = includeAll ? "?include=all" : "";
      return request<{ alerts: AlertRow[] }>(`/alerts${qs}`);
    },
    count: () => request<{ count: number }>("/alerts/count"),
    acknowledge: (id: number) =>
      request<{ ok: true }>(`/alerts/${id}/acknowledge`, { method: "POST" }),
    dismiss: (id: number) =>
      request<{ ok: true }>(`/alerts/${id}/dismiss`, { method: "POST" }),
    runCron: () => request<AlertGenSummary>("/alerts/run-cron", { method: "POST" }),
  },

  reference: {
    buildings: () => request<{ buildings: BuildingRef[] }>("/reference/buildings"),
    maintenance: () => request<{ maintenance: MaintenanceRef[] }>("/reference/maintenance"),
    units: (buildingId: number) =>
      request<{ units: UnitRef[] }>(`/reference/units?building=${buildingId}`),
    tenants: (buildingId: number) =>
      request<{ tenants: TenantRef[] }>(`/reference/tenants?building=${buildingId}`),
  },

  units: {
    list: (params: { building?: string; status?: string }) => {
      const qs = new URLSearchParams();
      if (params.building) qs.set("building", params.building);
      if (params.status) qs.set("status", params.status);
      return request<{ units: UnitListItem[] }>(`/units?${qs.toString()}`);
    },
    get: (id: number) => request<{ unit: UnitDetail }>(`/units/${id}`),
    update: (id: number, patch: UnitPatch) =>
      request<{ ok: true }>(`/units/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  },

  myWorkOrders: {
    list: () => request<{ workOrders: MyWorkOrderListItem[] }>("/my-work-orders"),
    get: (id: number) => request<{ workOrder: MyWorkOrderDetail }>(`/my-work-orders/${id}`),
    update: (id: number, body: MyWorkOrderUpdate) =>
      request<{ ok: true }>(`/my-work-orders/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    startTimer: (id: number) =>
      request<{ ok: true }>(`/my-work-orders/${id}/timer/start`, { method: "POST" }),
    stopTimer: (id: number, note?: string) =>
      request<{ ok: true }>(`/my-work-orders/${id}/timer/stop`, {
        method: "POST",
        body: JSON.stringify({ note }),
      }),
    addSupply: (id: number, body: { inventoryItemId: number; quantityUsed: number }) =>
      request<{ ok: true }>(`/my-work-orders/${id}/supplies`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    removeSupply: (id: number, supplyId: number) =>
      request<{ ok: true }>(`/my-work-orders/${id}/supplies/${supplyId}`, {
        method: "DELETE",
      }),
  },

  inventory: {
    list: (params: { building?: string; status?: string }) => {
      const qs = new URLSearchParams();
      if (params.building) qs.set("building", params.building);
      if (params.status) qs.set("status", params.status);
      return request<{ items: InventoryListItem[] }>(`/inventory?${qs.toString()}`);
    },
    get: (id: number) => request<{ item: InventoryItemDetail }>(`/inventory/${id}`),
    create: (body: InventoryCreate) =>
      request<{ id: number }>("/inventory", { method: "POST", body: JSON.stringify(body) }),
    update: (id: number, patch: InventoryPatch) =>
      request<{ ok: true }>(`/inventory/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },

  leads: {
    list: (params: { building?: string; status?: string }) => {
      const qs = new URLSearchParams();
      if (params.building) qs.set("building", params.building);
      if (params.status) qs.set("status", params.status);
      return request<{ leads: LeadWithNotes[] }>(`/leads?${qs.toString()}`);
    },
    get: (id: number) => request<{ lead: Lead }>(`/leads/${id}`),
    create: (body: LeadCreate) =>
      request<{ id: number }>("/leads", { method: "POST", body: JSON.stringify(body) }),
    update: (id: number, patch: LeadPatch) =>
      request<{ ok: true }>(`/leads/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    addNote: (id: number, note: string) =>
      request<{ ok: true }>(`/leads/${id}/notes`, {
        method: "POST",
        body: JSON.stringify({ note }),
      }),
  },

  tenants: {
    list: (params: { building?: string; status?: string }) => {
      const qs = new URLSearchParams();
      if (params.building) qs.set("building", params.building);
      if (params.status) qs.set("status", params.status);
      return request<{ tenants: TenantListItem[] }>(`/tenants?${qs.toString()}`);
    },
    get: (id: number) => request<{ tenant: TenantDetail }>(`/tenants/${id}`),
    // On an eviction match the server returns 409; the caller catches ApiError
    // (err.body holds { error: "eviction_match", match }).
    create: (body: TenantCreate) =>
      request<{ id: number; overridden: boolean }>("/tenants", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (id: number, patch: TenantPatch) =>
      request<{ ok: true }>(`/tenants/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    leaseAgreement: (id: number) =>
      request<{ pdfDataUrl: string; filename: string }>(`/tenants/${id}/lease-agreement`, {
        method: "POST",
      }),
    remove: (
      id: number,
      body: {
        reason: string;
        evictionDate?: string | null;
        reasonNotes?: string | null;
        amountOutstanding?: number | null;
      },
    ) =>
      request<{ ok: true; evictionListed: boolean }>(`/tenants/${id}/remove`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  evictionList: {
    list: (status: string) =>
      request<{ entries: EvictionEntry[] }>(
        `/eviction-list?${new URLSearchParams(status ? { status } : {}).toString()}`,
      ),
    lift: (id: number, reason: string) =>
      request<{ ok: true }>(`/eviction-list/${id}/lift`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
  },
};
